import React, { useState } from 'react';
import { Plus, Edit, Trash2, CreditCard, Pause, Play, History, RefreshCw, Search, Pencil } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatDate, formatPrice, formatPriceInput, parsePriceInput, daysUntilExpiry, getStatusBadgeClass, getStatusLabel } from '../utils/helpers';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { addDays, format, parseISO } from 'date-fns';

// 회원권 상품 폼
const ProductForm = ({ product, onClose }) => {
  const { addMembershipProduct, updateMembershipProduct } = useApp();
  const [form, setForm] = useState({
    name: product?.name || '',
    days: product?.days || 30,
    price: product?.price || '',
    type: product?.type || 'period',
    totalCount: product?.totalCount || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form, days: Number(form.days), price: Number(form.price) };
    if (form.totalCount) data.totalCount = Number(form.totalCount);
    if (product) updateMembershipProduct(product.id, data);
    else addMembershipProduct(data);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">상품명</label>
        <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="3개월 이용권" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">타입</label>
          <select className="input" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
            <option value="period">기간제</option>
            <option value="count">횟수제</option>
          </select>
        </div>
        <div>
          <label className="label">기간 (일)</label>
          <input type="number" className="input" value={form.days} onChange={e => setForm(p => ({ ...p, days: e.target.value }))} min="1" required />
        </div>
        <div>
          <label className="label">가격 (원)</label>
          <input type="text" inputMode="numeric" className="input" value={formatPriceInput(form.price)} onChange={e => setForm(p => ({ ...p, price: parsePriceInput(e.target.value) }))} required placeholder="88,000" />
        </div>
        {form.type === 'count' && (
          <div>
            <label className="label">이용 횟수</label>
            <input type="number" className="input" value={form.totalCount} onChange={e => setForm(p => ({ ...p, totalCount: e.target.value }))} min="1" placeholder="30" />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">{product ? '수정' : '등록'}</button>
      </div>
    </form>
  );
};

const PAYMENT_METHODS = ['현금', '카드', '지역화폐', '계좌이체', '기타'];
const CASH_RECEIPT_METHODS = ['현금', '계좌이체']; // 이 결제수단에서만 현금영수증 선택 표시

// 회원권 수정 폼 (만료일 조정 + 양도)
const EditMembershipForm = ({ membership, onClose }) => {
  const { members, updateMembership } = useApp();
  const [endDate, setEndDate] = useState(membership.endDate || '');
  const [transferSearch, setTransferSearch] = useState('');
  const [transferMemberId, setTransferMemberId] = useState('');

  const filteredMembers = members.filter(m =>
    transferSearch && (m.name?.includes(transferSearch) || m.phone?.includes(transferSearch)) && m.id !== membership.memberId
  );
  const transferTarget = members.find(m => m.id === transferMemberId);

  const handleSubmit = (e) => {
    e.preventDefault();
    const patch = {};
    if (endDate !== membership.endDate) patch.endDate = endDate;
    if (transferMemberId && transferTarget) {
      patch.memberId = transferMemberId;
      const fromMember = members.find(m => m.id === membership.memberId);
      const newEntry = {
        fromMemberId: membership.memberId,
        fromMemberName: fromMember?.name || '-',
        toMemberId: transferMemberId,
        toMemberName: transferTarget.name,
        transferredAt: new Date().toISOString(),
      };
      patch.transferHistory = [...(membership.transferHistory || []), newEntry];
    }
    if (Object.keys(patch).length > 0) updateMembership(membership.id, patch);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">만료일 수정</label>
        <input type="date" className="input" value={endDate} onChange={e => setEndDate(e.target.value)} />
        <p className="text-xs text-gray-400 mt-1">현재: {membership.endDate || '-'}</p>
      </div>
      <div>
        <label className="label">양도 대상 (다른 회원에게 넘기기, 선택)</label>
        {transferTarget ? (
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 mb-2">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {transferTarget.name} ({transferTarget.phone})
            </span>
            <button type="button" onClick={() => { setTransferMemberId(''); setTransferSearch(''); }} className="text-xs text-gray-400 hover:text-red-500">✕</button>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                className="input pl-9"
                placeholder="이름 또는 전화번호 검색..."
                value={transferSearch}
                onChange={e => setTransferSearch(e.target.value)}
              />
            </div>
            {transferSearch && (
              <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                {filteredMembers.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">검색 결과가 없습니다.</p>
                ) : (
                  filteredMembers.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => { setTransferMemberId(m.id); setTransferSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                    >
                      <span className="font-medium text-gray-900 dark:text-white">{m.name}</span>
                      <span className="text-gray-400 ml-2">{m.phone}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">저장</button>
      </div>
    </form>
  );
};

// 회원권 부여 폼
const AssignForm = ({ onClose }) => {
  const { members, membershipProducts, addMembership } = useApp();
  const [form, setForm] = useState({
    memberId: '',
    productId: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    price: '',
    paymentMethod: '',
    cashReceipt: '',
  });
  const [memberSearch, setMemberSearch] = useState('');
  const selectedMemberObj = members.find(m => m.id === form.memberId);
  const filteredMembers = members.filter(m =>
    !memberSearch || m.name?.includes(memberSearch) || m.phone?.includes(memberSearch)
  );

  const selectedProduct = membershipProducts.find(p => p.id === form.productId);
  const autoEndDate = selectedProduct && form.startDate
    ? format(addDays(parseISO(form.startDate), selectedProduct.days - 1), 'yyyy-MM-dd')
    : '';
  const [endDateOverride, setEndDateOverride] = useState('');
  const endDate = endDateOverride || autoEndDate;

  const handleProductChange = (productId) => {
    const p = membershipProducts.find(p => p.id === productId);
    setForm(prev => ({ ...prev, productId, price: p?.price || '' }));
    setEndDateOverride(''); // 상품 변경 시 자동계산으로 리셋
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.memberId || !form.productId) return;
    addMembership({
      memberId: form.memberId,
      productId: form.productId,
      productName: selectedProduct?.name || '',
      startDate: form.startDate,
      endDate,
      price: Number(form.price),
      paymentMethod: form.paymentMethod || null,
      cashReceipt: CASH_RECEIPT_METHODS.includes(form.paymentMethod) ? (form.cashReceipt || null) : null,
      status: 'active',
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">회원</label>
        {selectedMemberObj && (
          <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedMemberObj.name} ({selectedMemberObj.phone})
            </span>
            <button type="button" onClick={() => { setForm(p => ({ ...p, memberId: '' })); setMemberSearch(''); }} className="text-xs text-gray-400 hover:text-red-500">✕</button>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="이름 또는 전화번호 검색..."
            value={memberSearch}
            onChange={e => setMemberSearch(e.target.value)}
          />
        </div>
        {memberSearch && (
          <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
            {filteredMembers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-3">검색 결과가 없습니다.</p>
            ) : (
              filteredMembers.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { setForm(p => ({ ...p, memberId: m.id })); setMemberSearch(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{m.name}</span>
                  <span className="text-gray-400 ml-2">{m.phone}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <div>
        <label className="label">회원권 상품</label>
        <select className="input" value={form.productId} onChange={e => handleProductChange(e.target.value)} required>
          <option value="">상품 선택</option>
          {membershipProducts.map(p => <option key={p.id} value={p.id}>{p.name} - {formatPrice(p.price)}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">시작일</label>
          <input type="date" className="input" value={form.startDate} onChange={e => { setForm(p => ({ ...p, startDate: e.target.value })); setEndDateOverride(''); }} required />
        </div>
        <div>
          <label className="label">만료일{!endDateOverride && ' (자동계산)'}</label>
          <input
            type="date"
            className={`input ${!endDateOverride ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
            value={endDate}
            onChange={e => setEndDateOverride(e.target.value)}
          />
          {endDateOverride && (
            <button type="button" onClick={() => setEndDateOverride('')} className="text-xs text-primary-500 hover:underline mt-1">
              자동계산으로 되돌리기
            </button>
          )}
        </div>
        <div>
          <label className="label">결제 금액 (원)</label>
          <input type="text" inputMode="numeric" className="input" value={formatPriceInput(form.price)} onChange={e => setForm(p => ({ ...p, price: parsePriceInput(e.target.value) }))} placeholder="0" />
        </div>
        <div>
          <label className="label">결제 수단 (선택)</label>
          <select className="input" value={form.paymentMethod} onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value, cashReceipt: '' }))}>
            <option value="">선택 안함</option>
            {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {CASH_RECEIPT_METHODS.includes(form.paymentMethod) && (
          <div>
            <label className="label">현금영수증 (선택)</label>
            <select className="input" value={form.cashReceipt} onChange={e => setForm(p => ({ ...p, cashReceipt: e.target.value }))}>
              <option value="">선택 안함</option>
              <option value="발급">발급</option>
              <option value="미발급">미발급</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">부여</button>
      </div>
    </form>
  );
};

// 회원권 연장 폼
const RenewForm = ({ membership, onClose }) => {
  const { membershipProducts, addMembership } = useApp();
  const [productId, setProductId] = useState(membership.productId || '');
  const [price, setPrice] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [cashReceipt, setCashReceipt] = useState('');

  const selectedProduct = membershipProducts.find(p => p.id === productId);
  const [endDateOverride, setEndDateOverride] = useState('');

  // 연장 시작일: 기존 만료일 다음날 or 오늘 (만료된 경우)
  const days = daysUntilExpiry(membership.endDate);
  const startDate = days !== null && days >= 0
    ? (() => { const d = new Date(membership.endDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })()
    : new Date().toISOString().split('T')[0];

  const autoEndDate = selectedProduct
    ? (() => { const d = new Date(startDate); d.setDate(d.getDate() + selectedProduct.days - 1); return d.toISOString().split('T')[0]; })()
    : '';
  const endDate = endDateOverride || autoEndDate;

  const handleProductChange = (id) => {
    const p = membershipProducts.find(p => p.id === id);
    setProductId(id);
    setPrice(p?.price || '');
    setEndDateOverride('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productId) return;
    addMembership({
      memberId: membership.memberId,
      productId,
      productName: selectedProduct?.name || '',
      startDate,
      endDate,
      price: Number(price),
      paymentMethod: paymentMethod || null,
      cashReceipt: CASH_RECEIPT_METHODS.includes(paymentMethod) ? (cashReceipt || null) : null,
      status: 'active',
    });
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-300">
        기존 만료일 다음날부터 자동으로 시작됩니다. ({formatDate(startDate)})
      </div>
      <div>
        <label className="label">회원권 상품</label>
        <select className="input" value={productId} onChange={e => handleProductChange(e.target.value)} required>
          <option value="">상품 선택</option>
          {membershipProducts.map(p => <option key={p.id} value={p.id}>{p.name} - {formatPrice(p.price)}</option>)}
        </select>
      </div>
      {selectedProduct && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">시작일</label>
            <input type="date" className="input bg-gray-50 dark:bg-gray-600" value={startDate} readOnly />
          </div>
          <div>
            <label className="label">만료일{!endDateOverride && ' (자동계산)'}</label>
            <input
              type="date"
              className={`input ${!endDateOverride ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
              value={endDate}
              onChange={e => setEndDateOverride(e.target.value)}
            />
            {endDateOverride && (
              <button type="button" onClick={() => setEndDateOverride('')} className="text-xs text-primary-500 hover:underline mt-1">
                자동계산으로 되돌리기
              </button>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">결제 금액 (원)</label>
          <input type="text" inputMode="numeric" className="input" value={formatPriceInput(price)} onChange={e => setPrice(parsePriceInput(e.target.value))} placeholder="0" />
        </div>
        <div>
          <label className="label">결제 수단 (선택)</label>
          <select className="input" value={paymentMethod} onChange={e => { setPaymentMethod(e.target.value); setCashReceipt(''); }}>
            <option value="">선택 안함</option>
            {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {CASH_RECEIPT_METHODS.includes(paymentMethod) && (
          <div>
            <label className="label">현금영수증 (선택)</label>
            <select className="input" value={cashReceipt} onChange={e => setCashReceipt(e.target.value)}>
              <option value="">선택 안함</option>
              <option value="발급">발급</option>
              <option value="미발급">미발급</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="btn-primary">연장</button>
      </div>
    </form>
  );
};

// 일시정지 폼
const FreezeForm = ({ membership, onClose }) => {
  const { freezeMembership } = useApp();
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    freezeMembership(membership.id, reason);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
        정지 기간만큼 만료일이 자동으로 연장됩니다.
      </div>
      <div>
        <label className="label">정지 사유 (선택)</label>
        <input className="input" value={reason} onChange={e => setReason(e.target.value)} placeholder="부상, 해외 출장 등..." />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">취소</button>
        <button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium">일시정지</button>
      </div>
    </form>
  );
};

const Memberships = () => {
  const { membershipProducts, deleteMembershipProduct, memberships, members, deleteMembership, unfreezeMembership } = useApp();
  const [tab, setTab] = useState('list');
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [freezeTarget, setFreezeTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const sortedMemberships = [...memberships].sort((a, b) => b.createdAt?.localeCompare(a.createdAt || '') || 0);

  return (
    <div className="space-y-4">
      {/* 탭 */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[{ id: 'list', label: '회원권 현황' }, { id: 'products', label: '상품 설정' }].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 회원권 현황 */}
      {tab === 'list' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setIsAssignOpen(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              회원권 부여
            </button>
          </div>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    {['회원', '회원권', '시작일', '만료일', '상태', '결제금액', '결제수단', ''].map((h, i) => (
                      <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sortedMemberships.length === 0 ? (
                    <tr><td colSpan="8" className="text-center py-12 text-gray-400">회원권 데이터가 없습니다.</td></tr>
                  ) : sortedMemberships.map(ms => {
                    const member = members.find(m => m.id === ms.memberId);
                    const days = daysUntilExpiry(ms.endDate);
                    const isFrozen = ms.status === 'frozen' || !!ms.frozenAt;
                    const isExpired = !isFrozen && (ms.status === 'expired' || (days !== null && days < 0));
                    const isExpiring = !isExpired && !isFrozen && days !== null && days <= 7;
                    const displayStatus = isFrozen ? 'frozen' : isExpired ? 'expired' : isExpiring ? 'expiring' : 'active';
                    return (
                      <tr key={ms.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${isFrozen ? 'opacity-70' : ''}`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{member?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{ms.productName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDate(ms.startDate)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(ms.endDate)}
                          {isExpiring && <span className="ml-1 text-xs font-semibold text-yellow-600 dark:text-yellow-400">D-{days}</span>}
                          {isFrozen && <span className="ml-1 text-xs font-semibold text-blue-600 dark:text-blue-400">(정지중)</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={isFrozen ? 'badge badge-blue' : getStatusBadgeClass(displayStatus)}>
                            {isFrozen ? '정지중' : getStatusLabel(displayStatus)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatPrice(ms.price)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {ms.paymentMethod
                            ? ms.cashReceipt
                              ? <span>{ms.paymentMethod} <span className="text-xs text-green-600 dark:text-green-400">({ms.cashReceipt})</span></span>
                              : ms.paymentMethod
                            : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setEditTarget(ms)} className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="수정 (만료일/양도)">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => setRenewTarget(ms)} className="p-1.5 text-gray-400 hover:text-green-500 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors" title="연장">
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            {!isExpired && !isFrozen && (
                              <button onClick={() => setFreezeTarget(ms)} className="p-1.5 text-gray-400 hover:text-yellow-500 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors" title="일시정지">
                                <Pause className="w-4 h-4" />
                              </button>
                            )}
                            {isFrozen && (
                              <button onClick={() => unfreezeMembership(ms.id)} className="p-1.5 text-gray-400 hover:text-green-500 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors" title="정지 해제">
                                <Play className="w-4 h-4" />
                              </button>
                            )}
                            {(ms.freezeHistory?.length > 0 || ms.transferHistory?.length > 0) && (
                              <button onClick={() => setHistoryTarget(ms)} className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="변경 이력">
                                <History className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => setDeleteConfirm(ms)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 상품 설정 */}
      {tab === 'products' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setEditingProduct(null); setIsProductFormOpen(true); }} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              상품 등록
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {membershipProducts.map(product => (
              <div key={product.id} className="card p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingProduct(product); setIsProductFormOpen(true); }} className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteMembershipProduct(product.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{product.name}</h3>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 mt-1">{formatPrice(product.price)}</p>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <span className="badge-blue">{product.type === 'period' ? '기간제' : '횟수제'}</span>
                  <span className="badge-gray">{product.days}일</span>
                  {product.totalCount && <span className="badge-gray">{product.totalCount}회</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal isOpen={isProductFormOpen} onClose={() => { setIsProductFormOpen(false); setEditingProduct(null); }} title={editingProduct ? '상품 수정' : '회원권 상품 등록'}>
        <ProductForm product={editingProduct} onClose={() => { setIsProductFormOpen(false); setEditingProduct(null); }} />
      </Modal>

      <Modal isOpen={isAssignOpen} onClose={() => setIsAssignOpen(false)} title="회원권 부여">
        <AssignForm onClose={() => setIsAssignOpen(false)} />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="회원권 삭제"
        message="이 회원권을 삭제하시겠습니까?"
        onConfirm={() => { deleteMembership(deleteConfirm.id); setDeleteConfirm(null); }}
        onCancel={() => setDeleteConfirm(null)}
      />

      <Modal isOpen={!!editTarget} onClose={() => setEditTarget(null)} title="회원권 수정 (만료일/양도)">
        {editTarget && <EditMembershipForm membership={editTarget} onClose={() => setEditTarget(null)} />}
      </Modal>

      <Modal isOpen={!!renewTarget} onClose={() => setRenewTarget(null)} title="회원권 연장">
        {renewTarget && <RenewForm membership={renewTarget} onClose={() => setRenewTarget(null)} />}
      </Modal>

      <Modal isOpen={!!freezeTarget} onClose={() => setFreezeTarget(null)} title="회원권 일시정지">
        {freezeTarget && <FreezeForm membership={freezeTarget} onClose={() => setFreezeTarget(null)} />}
      </Modal>

      <Modal isOpen={!!historyTarget} onClose={() => setHistoryTarget(null)} title="변경 이력">
        {historyTarget && (
          <div className="space-y-4">
            {historyTarget.freezeHistory?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">일시정지 이력</p>
                <div className="space-y-2">
                  {historyTarget.freezeHistory.map((h, i) => (
                    <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm">
                      <div className="flex justify-between text-gray-700 dark:text-gray-300 mb-1">
                        <span>{formatDate(h.frozenAt)} ~ {formatDate(h.unfrozenAt)}</span>
                        <span className="font-semibold">{h.days}일 정지</span>
                      </div>
                      {h.reason && <p className="text-gray-400">사유: {h.reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {historyTarget.transferHistory?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">양도 이력</p>
                <div className="space-y-2">
                  {historyTarget.transferHistory.map((h, i) => (
                    <div key={i} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <span className="font-medium">{h.fromMemberName}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium text-blue-700 dark:text-blue-300">{h.toMemberName}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(h.transferredAt?.split('T')[0])}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button onClick={() => setHistoryTarget(null)} className="btn-secondary">닫기</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Memberships;
